import { Box, Stack, Typography } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DateTime } from "luxon";

export type DueDateShiftReviewer = {
  id: string;
  name?: string;
  email?: string;
  dueDate?: DateTime | null;
};

type Props = {
  open: boolean;
  /** Dismissal (Escape/backdrop/X) — should abandon the shift, not save. */
  onClose: () => void;
  /** "Keep as-is" — the explicit choice to save without shifting. */
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Whole calendar days to shift each reviewer due date by (submission delta).
   * Days rather than milliseconds so a DST transition inside either range can't
   * land the result an hour short and roll the date back a day.
   */
  deltaDays: number;
  /** Reviewers whose due dates would move; signer dates are intentionally excluded. */
  reviewers: DueDateShiftReviewer[];
  loading?: boolean;
};

/**
 * Confirms shifting reviewer due dates by the same delta as a submission
 * due-date change, with a per-reviewer before/after preview.
 */
export function ReviewerDueDateShiftDialog({
  open,
  onClose,
  onCancel,
  onConfirm,
  deltaDays,
  reviewers,
  loading,
}: Props) {
  const shiftable = reviewers.filter((r) => r.dueDate?.isValid);
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onCancel={onCancel}
      onConfirm={onConfirm}
      loading={loading}
      header="Move reviewer due dates?"
      confirmText="Move reviewer dates"
      cancelText="Keep as-is"
    >
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        You changed the submission due date. Do you want to also shift reviewer due dates the same
        number of days? Signer due dates won't change.
      </Typography>
      <Stack spacing={1}>
        {shiftable.map((r) => (
          <Box key={r.id} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="body2" fontWeight={500}>
              {r.name || r.email || "Reviewer"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {r.dueDate?.toFormat("MM/dd/yyyy")} →{" "}
              {r.dueDate?.plus({ days: deltaDays }).toFormat("MM/dd/yyyy")}
            </Typography>
          </Box>
        ))}
      </Stack>
    </ConfirmDialog>
  );
}
